import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { uploadImage } from "../Utils/imageService";
import { useAuth } from "../context/AuthContext";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  margin-bottom: 4px;
`;

const ModeButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  font-size: 0.8rem;
  cursor: pointer;
  border: none;
  background: ${({ $active, theme }) =>
    $active
      ? theme.colors.accent.primary
      : theme.colors.background.surfaceAlt};
  color: ${({ $active, theme }) =>
    $active
      ? theme.colors.text.inverse
      : theme.colors.text.secondary};
  transition: background 0.2s, color 0.2s;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DropZone = styled.div`
  border: 2px dashed
    ${({ $dragOver, $hasError, theme }) =>
      $hasError
        ? theme.colors.accent.danger
        : $dragOver
        ? theme.colors.accent.primary
        : theme.colors.border.default};
  border-radius: 8px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: ${({ $dragOver, theme }) =>
    $dragOver
      ? theme.colors.state.hover
      : theme.colors.background.surfaceAlt};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const DropText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HiddenInput = styled.input`
  display: none;
`;

const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`;

const PreviewImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const UrlInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  border-radius: 6px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.input.bg};
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.input.placeholder};
  }
`;

const UploadButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.accent.danger};
    color: ${({ theme }) => theme.colors.text.inverse};
    border-color: ${({ theme }) => theme.colors.accent.danger};
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.accent.danger};
`;

const StatusText = styled.p`
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FileName = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  display: inline-block;
`;

const ImageUpload = ({
  onUpload,
  currentUrl = "",
  folder = "general",
  label = "Image",
  isAuthenticated: isAuthenticatedProp,
  allowUrl = true,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const canUpload =
    typeof isAuthenticatedProp === "boolean"
      ? isAuthenticatedProp
      : !!auth?.isAuthenticated;
  // Uploading is the action almost everyone wants; pasting a hosted URL is the admin escape
  // hatch. Defaulting to "url" made the control look like it had no upload at all.
  const [mode, setMode] = useState(canUpload || !allowUrl ? "upload" : "url");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [urlValue, setUrlValue] = useState(currentUrl);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUrlValue(currentUrl || "");
    // Drop any file staged against the previous record. Without this the picker keeps a file
    // chosen for one farm/product while the form has already switched to another, and pressing
    // Upload then attaches that image to the wrong record — an image nobody uploaded for it.
    setFile(null);
    setPreview("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [currentUrl]);

  useEffect(() => {
    if (!canUpload && allowUrl && mode === "upload") {
      setMode("url");
    }
  }, [allowUrl, canUpload, mode]);

  const validateFile = useCallback((f) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      return t("image_upload.invalid_type", { defaultValue: "Invalid file type. Allowed: JPEG, PNG, WebP, GIF." });
    }
    if (f.size > MAX_FILE_SIZE) {
      return t("image_upload.too_large", { defaultValue: "File size exceeds 5 MB limit." });
    }
    return null;
  }, [t]);

  const handleFileSelect = useCallback(
    (f) => {
      setError("");
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(f);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    },
    [validateFile]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const droppedFile = e.dataTransfer?.files?.[0];
      if (droppedFile) handleFileSelect(droppedFile);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const selected = e.target.files?.[0];
      if (selected) handleFileSelect(selected);
    },
    [handleFileSelect]
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setError("");

    if (!canUpload) {
      setError(t("image_upload.login_required", { defaultValue: "You must be logged in to upload images." }));
      return;
    }

    setUploading(true);

    try {
      const data = await uploadImage(file, folder, auth?.token);
      if (data?.url) {
        onUpload(data.url);
        setFile(null);
        setPreview("");
        setUrlValue(data.url);
      } else {
        setError(t("image_upload.no_url", { defaultValue: "Upload failed. No URL returned." }));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || t("image_upload.failed", { defaultValue: "Upload failed. Please try again." });
      setError(msg);
    } finally {
      setUploading(false);
    }
  }, [auth?.token, canUpload, file, folder, onUpload, t]);

  const handleUrlChange = useCallback(
    (e) => {
      const val = e.target.value;
      setUrlValue(val);
      onUpload(val);
    },
    [onUpload]
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setPreview("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const displayUrl = currentUrl || urlValue;

  return (
    <Wrapper>
      {label && <Label>{label}</Label>}

      {allowUrl && <ModeToggle>
        <ModeButton
          type="button"
          $active={mode === "url"}
          onClick={() => setMode("url")}
        >
          {t("image_upload.paste_url", { defaultValue: "Paste URL" })}
        </ModeButton>
        <ModeButton
          type="button"
          $active={mode === "upload"}
          onClick={() => canUpload && setMode("upload")}
          disabled={!canUpload}
        >
          {t("image_upload.upload_file", { defaultValue: "Upload file" })}
        </ModeButton>
      </ModeToggle>}

      {allowUrl && mode === "url" && (
        <>
          <UrlInput
            type="url"
            placeholder="https://example.com/image.jpg"
            value={urlValue}
            onChange={handleUrlChange}
          />
          {displayUrl && (
            <PreviewContainer>
              <PreviewImage
                src={displayUrl}
                alt={t("image_upload.preview_alt", { defaultValue: "Preview" })}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </PreviewContainer>
          )}
        </>
      )}

      {mode === "upload" && (
        <>
          <DropZone
            $dragOver={dragOver}
            $hasError={!!error}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <DropText>
              {file
                ? t("image_upload.file_selected", { defaultValue: "File selected. Click Upload to proceed." })
                : t("image_upload.dropzone", { defaultValue: "Drag & drop an image here, or click to browse." })}
            </DropText>
            <DropText style={{ fontSize: "0.75rem", marginTop: 4 }}>
              {t("image_upload.constraints", { defaultValue: "Max 5 MB - JPEG, PNG, WebP, GIF" })}
            </DropText>
          </DropZone>

          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleInputChange}
          />

          {file && (
            <PreviewContainer>
              {preview && (
                <PreviewImage src={preview} alt={t("image_upload.preview_alt", { defaultValue: "Preview" })} />
              )}
              <FileName>{file.name}</FileName>
              <UploadButton
                type="button"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading
                  ? t("image_upload.uploading", { defaultValue: "Uploading…" })
                  : t("image_upload.upload", { defaultValue: "Upload" })}
              </UploadButton>
              <RemoveButton type="button" onClick={handleRemoveFile}>
                {t("image_upload.remove", { defaultValue: "Remove" })}
              </RemoveButton>
            </PreviewContainer>
          )}

          {uploading && (
            <StatusText>{t("image_upload.uploading_status", { defaultValue: "Uploading image…" })}</StatusText>
          )}

          {displayUrl && !file && (
            <PreviewContainer>
              <PreviewImage src={displayUrl} alt={t("image_upload.current", { defaultValue: "Current image" })} />
              <StatusText style={{ margin: 0 }}>{t("image_upload.current", { defaultValue: "Current image" })}</StatusText>
            </PreviewContainer>
          )}
        </>
      )}

      {!canUpload && (
        <StatusText>
          {t("image_upload.sign_in_hint", { defaultValue: "Sign in to upload files, or paste a hosted image URL." })}
        </StatusText>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

export { ImageUpload };
export default ImageUpload;
